<script lang="ts" setup>
import config from '@/config';
import { useGlobalProperties } from '@/src/composables/useGlobalProperties';
import { useKlaviyo } from '@/src/composables/useKlaviyo';
import { usePageContext } from '@/src/composables/usePageContext';
import SeAdvisorAdvisorsList from '@/src/modules/advisor/components/advisors-list.vue';
import SeImg from '@/src/ui-kit/img/Img.vue';
import { theme } from '@/theme';
import { onMounted, ref } from 'vue';

const { general } = theme;
const { getGlobalProperties } = useGlobalProperties();
const { $api } = getGlobalProperties();
const { trackSpecificEvent } = useKlaviyo();
const trackCategory = trackSpecificEvent('Viewed Category');
const pageContext = usePageContext();
const categoryName: string = pageContext.routeParams.category_name || '';

const category = ref(null);

const colorSchemes: Record<string, string> = {
  feature: 'from-feature-10',
  love: 'from-love-10',
  warning: 'from-warning-10',
  success: 'from-success-10',
  primary: 'from-primary-10',
};

const fetchCategory = async () => {
  await $api
    .get(`/user/category/${categoryName}`)
    .then(({ data }) => {
      category.value = data;
    })
    .catch((err) => {
      pageContext.dataIsMissing = true;
    });
};

await fetchCategory();

onMounted(() => {
  trackCategory({ category_name: categoryName });
});
</script>

<script lang="ts">
export default {
  name: 'SeAdvisorCategoryPage',
};
</script>

<template>
  <section
    v-if="category"
    class="pt-32 md:py-56 lg:py-88 bg-gradient-to-b to-white"
    :class="colorSchemes[category.color]"
  >
    <div class="container">
      <div class="flex flex-wrap flex-row-reverse justify-between items-center">
        <div class="w-full md:w-1/3 pb-16 md:pb-0">
          <div class="flex justify-start md:justify-end">
            <div v-if="category.icon">
              <suspense>
                <se-img
                  :alt="category.title"
                  :src="`/static/images/category/${config.theme.name}/${category.icon}.svg`"
                  class="w-88 h-88 md:w-200 md:h-200"
                />
              </suspense>
            </div>
          </div>
        </div>
        <div class="w-full md:w-2/3">
          <h1 class="text-h-xl md:text-h-3xl font-bold pb-8 md:pb-16">
            {{ category.title }}
          </h1>
          <p class="text-b-s md:text-b-xl md:font-medium">
            {{ category.description }}
          </p>
        </div>
      </div>
    </div>
  </section>
  <section v-if="category" class="py-32 md:py-56 lg:py-88">
    <suspense>
      <se-advisor-advisors-list :query="{ categories: [category.id] }" :has-paging="true">
        <template #title="{ total, loading }">
          <h1 class="text-h-l md:text-h-2xl font-semibold">
            <span v-if="!loading"> {{ total }} {{ general.advisors }}</span>
          </h1>
        </template>
      </se-advisor-advisors-list>
    </suspense>
  </section>
  <section v-else>
    <div class="text-center my-64">
      <span class="text-b-xl">Category not found</span>
      <br />
      <a href="/" class="btn btn-info">Back to home page</a>
    </div>
  </section>
</template>
